import { Button } from "@monsterindia/global-ui-components";
import { Modal } from "@monsterindia/global-ui-components";
import dynamic from "next/dynamic";
import React, { useEffect } from "react";

import { useRecommendedPageData } from "../";
import { useDashboardContext } from "../..";
import {
  RecommendedJobAPIDataType,
  RecommendedPageContextProps,
  RecommendedPageDataType,
} from "../types";

const ApplyJob = dynamic<{
  jobData: RecommendedJobAPIDataType;
  siteProperties: sitePropsType | undefined;
  pageName: string;
  btnClass: string;
  isLoggedIn: boolean;
  isSyndicateQuickApplyEnabled: boolean;
  isMobile: boolean;
  applyBtnType?: string;
  applyBtnConfigure?: {
    fetch?: boolean;
    selected?: boolean;
    reset?: boolean;
    isJobApplied?: boolean;
  };
  isJobApplied: boolean;
  doNotOpenConsecutiveApops?: boolean;
  inputHandler?: (data: {
    selected: boolean;
    id: string;
    reset: boolean;
  }) => void;
  inputResponse?: (data: any) => void;
  inputReset?: (data: any) => void;
  inputSelection: boolean;
  bulkApplyBtnData?: {
    label: string;
    modifier: string;
    url: string;
    className: string;
    size: string;
    jobIds: Array<number>;
  };
}>(() => import("@monsterindia/seeker-jobs").then((mod) => mod.ApplyJob), {
  ssr: false,
});

interface DashboardPropsType {
  isMobile: boolean;
}

const MultiSelectDiscard = () => {
  const {
    recommendedJobData,
    setRecommendedJobData,
    handleEventTracking,
    cleverTapPropertyValues,
  }: RecommendedPageContextProps = useRecommendedPageData();

  const { isMobile }: DashboardPropsType = useDashboardContext();
  const isSyndicateQuickApplyEnabled =
    recommendedJobData?.windowData?.isSyndicateQuickApplyEnabled || false;

  const inputHandlerBulkApply = (inputResponseData: any) => {
    const statusSuccess =
      inputResponseData.status != 400 ? "success" : "failed";
    setRecommendedJobData((prevData: RecommendedPageDataType) => {
      let modifiedData = {
        ...prevData,
      };
      if (
        inputResponseData?.apiResponse?.nextActionRequired &&
        Object.keys(inputResponseData?.apiResponse?.nextActionRequired).length >
          0
      ) {
        if (!modifiedData.nudgeData)
          modifiedData.nudgeData =
            inputResponseData?.apiResponse?.nextActionRequired;

        if (!modifiedData.popUpOpened) modifiedData.popUpOpened = true;
      }
      const totalSuccessfulBulkApply = statusSuccess == "success";

      modifiedData.totalSuccessfulBulkApplyData = {
        fetchDone: statusSuccess,
        totalSuccessfulBulkApply: totalSuccessfulBulkApply
          ? modifiedData.multiSelectCount
          : 0,
        totalBulkApply: modifiedData.multiSelectCount,
      };

      return modifiedData;
    });
  };

  const handleContinueBulkApply = () => {
    setRecommendedJobData((prevData: RecommendedPageDataType) => {
      const savedJobsModified = {
        ...prevData,
        discardBulkApplyPopup: {
          ...prevData.discardBulkApplyPopup,
          decision: true,
          value: false,
        },
      };
      return savedJobsModified;
    });
  };

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .guic-modal-container {
        max-width: 430px !important;
      }
      .guic-modal-content {
        padding-bottom: 8px !important;
        border-radius: 1rem !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <Modal
      showModal={recommendedJobData?.discardBulkApplyPopup?.value}
      closeModal={() => {}}
      screenAlignment={isMobile ? "bottom-sheet" : "center"}
      // style={{ maxHeight: "100%", borderRadius: "0px", padding: "0px" }}
      showCloseButton={false}
      transition={false}
      customContentContainerClass="!max-w-[430px]"
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h4 className="font-sans text-base font-bold leading-6 text-black">
            Hold on! ✋ You've {recommendedJobData?.multiSelectCount || 0}{" "}
            {`${recommendedJobData.multiSelectCount > 1 ? "jobs" : "job"}`}{" "}
            selected.
          </h4>
          <p className="text-sm font-normal leading-5 text-[var(--Content-Tertiary,#777584)]">
            If you leave now, your selection will be cleared. Apply now before
            moving on?
          </p>
        </div>
        <div className="!-ml-4 !-mr-4 flex justify-center gap-4 bg-white px-5 pt-2 shadow-[0px_-4px_8px_-2px_rgba(0,0,0,0.08),_0px_-2px_2px_-2px_rgba(0,0,0,0.15)]">
          <Button
            label={"Continue"}
            modifier="tertiary"
            onClick={() => {
              handleContinueBulkApply();
            }}
            size="lg"
            url=""
            className="!h-[44px] !w-full"
          />
          <div className="!h-[44px] !w-full">
            <ApplyJob
              key={"bulkApplyBtn"}
              jobData={{} as RecommendedJobAPIDataType}
              siteProperties={recommendedJobData.sitePropsProperties}
              isSyndicateQuickApplyEnabled={isSyndicateQuickApplyEnabled}
              pageName={"Dashboard"}
              btnClass={"!h-auto md:!h-auto !px-2 !py-[6px] !text-[12px]"}
              inputResponse={(inputResponseData: any) => {
                inputHandlerBulkApply(inputResponseData);
              }}
              isLoggedIn={true}
              isMobile={isMobile}
              applyBtnType="bulkapply"
              doNotOpenConsecutiveApops={true}
              isJobApplied={false}
              inputSelection={
                recommendedJobData?.allMultiSelectSelected || false
              }
              bulkApplyBtnData={{
                label: `Quick Apply (${recommendedJobData.multiSelectCount})`,
                modifier: "primary",
                url: "",
                className: "!h-[44px] !w-full",
                size: "lg",
                jobIds: recommendedJobData?.multiSelectJobIds || [],
              }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(MultiSelectDiscard);
